<template>
  <div class="leave-apply">
    <van-nav-bar
      :title="childName + '请假'"
      left-arrow
      @click-left="goBack"
      fixed
      placeholder
    />

    <div class="form-area">
      <van-field
        v-model="form.content"
        rows="3"
        autosize
        type="textarea"
        maxlength="20"
        placeholder="请输入请假原因"
        show-word-limit
        class="reason-field"
      />

      <!-- 日期选择区域 -->
      <div class="date-selector" @click="showCalendar = true">
        <span class="date-label">请假日期</span>
        <div class="date-tags-wrap" v-if="selectedDates.length > 0">
          <span class="date-tag" v-for="d in selectedDates" :key="d">{{ d }}</span>
          <span class="date-count">共{{ selectedDates.length }}天</span>
        </div>
        <span class="date-placeholder" v-else>点击选择日期</span>
        <van-icon name="arrow" class="date-arrow" />
      </div>
    </div>

    <div class="tips">
      <p>*每次最多请7天假（未来30天内）</p>
      <p>*当天17点之后将不能提交当天的请假</p>
    </div>

    <van-calendar
      v-model:show="showCalendar"
      type="multiple"
      :min-date="minDate"
      :max-date="maxDate"
      :max-range="7"
      color="#52C1B2"
      @confirm="onDateConfirm"
      :formatter="formatter"
    />

    <div class="bottom-action">
      <van-button type="primary" block round :disabled="submitting" @click="handleSubmit">
        提交请假
      </van-button>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Toast, Dialog } from "vant";
import api from "../service/api";

export default {
  setup() {
    const route = useRoute();
    const router = useRouter();
    // 家长走 sessionStorage；班主任通过 query 传入学生
    const childName = ref(
      route.query.studentName || sessionStorage.getItem("childName") || "孩子"
    );
    const childId = route.query.studentId || sessionStorage.getItem("childId");
    const showCalendar = ref(false);
    const submitting = ref(false);
    const selectedDates = ref([]);

    const form = ref({
      content: "",
    });
    const dateText = ref("");

    // 日历日期范围
    const today = new Date();
    const minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30);

    // 格式化日期为 YYYY-MM-DD
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return y + "-" + m + "-" + d;
    };

    // 家长端 17 点门禁：当天17点后不能再请当天的假（班主任代请 role!=1 不受限）
    const isParent = sessionStorage.getItem("role") == 1;
    const isAfterFivePM = () => new Date().getHours() >= 17;

    // 日历底部显示提示
    const formatter = (day) => {
      return day;
    };

    // 确认日期选择
    const onDateConfirm = (dates) => {
      showCalendar.value = false;
      if (!dates || dates.length == 0) {
        selectedDates.value = [];
        dateText.value = "";
        return;
      }
      // 最多请 7 天：超过时取前 7 个（与小程序端 leave.js 口径一致）
      // ponytail: van-calendar 的 max-range 仅对 type=range 生效，multiple 需手动截断
      if (dates.length > 7) {
        Toast("最多只能选择 7 天，已自动取前 7 天");
        dates = dates.slice(0, 7);
      }
      // 家长 17 点后不能再请当天：自动从选中集合里去掉今天
      if (isParent && isAfterFivePM()) {
        const todayStr = formatDate(new Date());
        const filtered = dates.filter((d) => formatDate(d) !== todayStr);
        if (filtered.length !== dates.length) {
          Toast("当天17点后不能请当天的假，已自动去掉今天");
          dates = filtered;
        }
      }
      if (!dates || dates.length == 0) {
        selectedDates.value = [];
        dateText.value = "";
        return;
      }
      selectedDates.value = dates.sort((a, b) => a - b).map(formatDate);
      dateText.value = selectedDates.value.join("、");
    };

    // 提交请假
    const handleSubmit = () => {
      if (!form.value.content.trim()) {
        Toast("请输入请假原因");
        return;
      }
      if (selectedDates.value.length == 0) {
        Toast("请选择请假日期");
        return;
      }
      // 家长 17 点门禁兜底：选好日期后页面停留跨过 17 点再提交的场景
      if (
        isParent &&
        isAfterFivePM() &&
        selectedDates.value.includes(formatDate(new Date()))
      ) {
        Toast("当天17点后不能请当天的假，请去掉今天再提交");
        return;
      }

      Dialog.alert({
        title: "确认请假",
        message: "确定要为" + childName.value + "提交请假申请吗？",
        showCancelButton: true,
      })
        .then(() => {
          submitLeave();
        })
        .catch(() => {});
    };

    const submitLeave = async () => {
      try {
        submitting.value = true;
        // 请假发起方：1=家长自助，2=教职工代请（老师/校长等非家长角色统一按 2）
        const leaveType = sessionStorage.getItem("role") == 1 ? "1" : "2";
        const res = await api.addLeave({
          sid: localStorage.getItem("sid"),
          user_id: sessionStorage.getItem("userId"),
          student_id: childId,
          content: form.value.content.trim(),
          dates: selectedDates.value.join(","),
          type: leaveType,
        });
        if (res.data.code == 1001) {
          // 成功不解锁：submitting 保持 true，按钮一直置灰到离开页面，杜绝重复提交请假
          // 与小程序端一致：弹窗确认后再返回，避免 Toast 一闪而过用户没看见
          Dialog.alert({
            title: "请假成功",
            message: "请假申请已提交，请等待审核",
            confirmButtonText: "我知道了",
            confirmButtonColor: "#52c1b2",
          }).then(() => {
            router.back();
          });
        } else {
          submitting.value = false;
          Toast(res.data.message || "提交失败");
        }
      } catch (error) {
        submitting.value = false;
        Toast("提交失败，请重试");
      }
    };

    const goBack = () => router.back();

    return {
      childName,
      form,
      dateText,
      showCalendar,
      submitting,
      minDate,
      maxDate,
      formatter,
      onDateConfirm,
      handleSubmit,
      goBack,
      selectedDates,
    };
  },
};
</script>

<style lang="less" scoped>
.leave-apply {
  min-height: 100vh;
  background: #f2f2f2;
  padding-bottom: 80px;
}

.form-area {
  margin-top: 10px;
}

.reason-field {
  background: #fff;
}

/* 日期选择区域 */
.date-selector {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  background: #fff;
  padding: 12px 16px;
  margin-top: 1px;
  cursor: pointer;
  min-height: 52px;
}

.date-label {
  font-size: 14px;
  color: #323233;
  flex-shrink: 0;
  min-width: 56px;
}

.date-tags-wrap {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.date-tag {
  display: inline-block;
  padding: 3px 10px;
  background: var(--color-primary-light);
  color: var(--color-primary);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.date-count {
  font-size: 12px;
  color: #999;
  margin-left: 4px;
}

.date-placeholder {
  flex: 1;
  font-size: 14px;
  color: #c8c9cc;
}

.date-arrow {
  color: #c8c9cc;
  flex-shrink: 0;
  margin-left: auto;
}

.tips {
  padding: 15px;
  font-size: 12px;
  color: #999;
  line-height: 1.8;
}

.bottom-action {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 15px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}
</style>
